import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { TailSpin } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import Button from "../../tailwindUI/Button";
import SuccessMessage from "../../tailwindUI/SuccessMessage";
import { updateShowStock } from "../../Services/DirectusService";

const fetchWithTimeout = (fetchPromise, timeout = 30000) => {
  return Promise.race([
    fetchPromise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeout)
    ),
  ]);
};

const fetchHiddenStockAPI = async (userToken) => {
  const myHeaders = new Headers();
  myHeaders.append("Authorization", process.env.REACT_APP_AUTH_TOKEN);
  myHeaders.append("Content-Type", "application/json");

  const raw = JSON.stringify({
    UserToken: userToken,
  });

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: raw,
    redirect: "follow",
  };

  const fetchPromise = fetch(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/stockone/am/AMHiddenStockReport`,
    requestOptions
  ).then((response) => response.json());

  const result = await fetchWithTimeout(fetchPromise, 30000);
  return result;
};

const SelectiveShowStockModalV2 = ({ open, setOpen, onSuccess, userToken }) => {
  const cancelButtonRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Component loaded
  }, []);

  const [hiddenStock, setHiddenStock] = useState([]);
  const [filteredStock, setFilteredStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [selectedVins, setSelectedVins] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [apiFailled, setApiFailled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const LogOut = () => {
    localStorage.removeItem("UserName");
    localStorage.removeItem("FirstName");
    localStorage.removeItem("LastName");
    localStorage.removeItem("UserToken");
    localStorage.removeItem("UserRole");
    localStorage.removeItem("Location");
    localStorage.removeItem("Email");
    localStorage.removeItem("UserLoggedin");
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!open) return;

      setLoading(true);
      setFetchError(false);

      try {
        const result = await fetchHiddenStockAPI(userToken);
        
        if (
          result.status_code === 200 &&
          result.status === "Success" &&
          result.data === "Invalid User Token, The token may have expired, You may need to re-login" &&
          result.message === "Invalid User Token, The token may have expired, You may need to re-login"
        ) {
          LogOut();
          return;
        }
        
        if (result.status_code === 200 && result.status === "Success") {
          let stockData = result.data || [];
          
          if (stockData && typeof stockData === 'object' && !Array.isArray(stockData)) {
            stockData = Object.values(stockData);
          }
          
          const mappedData = stockData.map(item => ({
            VIN: item.win_num || item.VIN || 'N/A',
            Model: item.model_name || item.Model || 'N/A',
            Fuel: item.fueltype || item.Fuel || 'N/A',
            Variant: item.variant || item.Variant || 'N/A',
            Colour: item.colour || item.Colour || 'N/A',
            Type: item.carstatus || item.Type || 'N/A',
            YOM: item.yom || item.YOM || 'N/A',
            Status: item.status || item.Status || '',
            AllocationStatus: item.allocation_status || item.AllocationStatus || '',
            Outlet: item.Outlet || '',
          }));
          
          setHiddenStock(mappedData);
          setFilteredStock(mappedData);
        } else {
          setFetchError(true);
        }
      } catch (error) {
        if (error.message === "Request timed out") {
          setError("Request timed out. Please try again.");
        }
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, userToken]);

  const handleClose = () => {
    setOpen(false);
    setSelectedVins(new Set());
    setSuccessMsg("");
    setError(null);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleCancel = () => {
    setOpen(false);
    setSelectedVins(new Set());
    setSuccessMsg("");
    setError(null);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleCheckboxChange = (vin) => {
    setSelectedVins((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(vin)) {
        newSelected.delete(vin);
      } else {
        newSelected.add(vin);
      }
      return newSelected;
    });
  };

  const totalPages = Math.ceil(filteredStock.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStock = filteredStock.slice(startIndex, endIndex);

  const handleSelectAll = () => {
    const currentPageVins = paginatedStock.map(variant => variant.VIN);
    const allCurrentPageSelected = currentPageVins.every(vin => selectedVins.has(vin));
    
    setSelectedVins((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (allCurrentPageSelected) {
        currentPageVins.forEach(vin => newSelected.delete(vin));
      } else {
        currentPageVins.forEach(vin => newSelected.add(vin));
      }
      return newSelected;
    });
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredStock(hiddenStock);
    } else {
      const filtered = hiddenStock.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        return (
          item.Model?.toLowerCase().includes(searchLower) ||
          item.Fuel?.toLowerCase().includes(searchLower) ||
          item.Variant?.toLowerCase().includes(searchLower) ||
          item.Colour?.toLowerCase().includes(searchLower) ||
          item.VIN?.toLowerCase().includes(searchLower) ||
          item.Type?.toLowerCase().includes(searchLower) ||
          item.YOM?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredStock(filtered);
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSubmit = async () => {
    const vinsArray = Array.from(selectedVins);

    setSubmitting(true);
    setError(null);
    setSuccessMsg("");

    try {
      const result = await updateShowStock(vinsArray);

      if (result.success) {
        setSuccessMsg(result.message || "Stock visibility updated successfully");
        setApiFailled(false);
        
        setTimeout(() => {
          setOpen(false);
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      } else {
        setError(result.message || "Failed to update stock visibility. Please try again.");
        setApiFailled(true);
      }
    } catch (error) {
      setError("Network error. Please check your connection and try again.");
      setApiFailled(true);
    } finally {
      setSubmitting(false);
    }
  };

  const successMessage = () => {
    setTimeout(() => {
      setSuccessMsg("");
    }, 3000);
    return successMsg;
  };

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl max-h-[90vh] flex flex-col">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Hidden Stock Detail
                    </Dialog.Title>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={handleClose}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {!loading && !fetchError && hiddenStock.length > 0 && (
                    <div className="mt-4">
                      {error && (
                        <div className="mb-3 rounded-md bg-red-50 p-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      )}
                      <div className="flex gap-3 mb-4">
                        <Button
                          buttonText={submitting ? "Submitting..." : "Show Selected Stock"}
                          onClick={handleSubmit}
                          type="redFilled"
                          disabled={selectedVins.size === 0 || submitting}
                        />
                        <Button
                          buttonText="Cancel"
                          onClick={handleCancel}
                          type="whiteButton"
                          disabled={submitting}
                        />
                      </div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Search Criteria</h4>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Enter Model, Fuel, Variant, Color, YOM, VIN, Status"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <button
                          onClick={handleSearch}
                          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                        >
                          Search
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {startIndex + 1} of {filteredStock.length} results
                      </p>
                    </div>
                  )}
                </div>

                <div className="px-4 pb-4 sm:px-6 sm:pb-4 overflow-y-auto flex-1">
                  {loading && (
                    <div className="flex justify-center items-center py-8">
                      <TailSpin height="40" width="40" color="#dc2626" />
                    </div>
                  )}
                  {fetchError && !loading && (
                    <div className="text-center py-4">
                      <p className="text-sm text-red-600 mb-3">
                        {error || "Failed to load hidden stock. Please try again."}
                      </p>
                      <Button
                        buttonText="Retry"
                        onClick={() => window.location.reload()}
                        type="redFilled"
                      />
                    </div>
                  )}
                  {!loading && !fetchError && hiddenStock.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">
                      No hidden stock available.
                    </p>
                  )}
                  {!loading && !fetchError && hiddenStock.length > 0 && (
                    <div className="mt-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                                <input
                                  type="checkbox"
                                  checked={paginatedStock.length > 0 && paginatedStock.every(v => selectedVins.has(v.VIN))}
                                  onChange={handleSelectAll}
                                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                />
                              </th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Model</th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Fuel</th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Variant</th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Colour</th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">VIN No.</th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">Type</th>
                              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">YOM</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedStock.map((variant) => {
                              const isSelected = selectedVins.has(variant.VIN);
                              return (
                                <tr key={variant.VIN} className={`${isSelected ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm border-r border-gray-200">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleCheckboxChange(variant.VIN)}
                                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                    />
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">{variant.Model}</td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">{variant.Fuel}</td>
                                  <td className="px-3 py-4 text-sm text-gray-900 border-r border-gray-200">{variant.Variant}</td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">{variant.Colour}</td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-mono border-r border-gray-200">{variant.VIN}</td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">{variant.Type}</td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">{variant.YOM}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                            <div>
                              <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(endIndex, filteredStock.length)}</span> of{' '}
                                <span className="font-medium">{filteredStock.length}</span> results
                              </p>
                            </div>
                            <div>
                              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                                <button
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Previous
                                </button>
                                {[...Array(totalPages)].map((_, index) => {
                                  const page = index + 1;
                                  if (
                                    page === 1 ||
                                    page === totalPages ||
                                    (page >= currentPage - 1 && page <= currentPage + 1)
                                  ) {
                                    return (
                                      <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                          page === currentPage
                                            ? 'z-10 bg-indigo-600 text-white'
                                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                        }`}
                                      >
                                        {page}
                                      </button>
                                    );
                                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                                    return <span key={page} className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300">...</span>;
                                  }
                                  return null;
                                })}
                                <button
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Next
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
      {successMsg && (
        <SuccessMessage
          onClose={() => setSuccessMsg("")}
          message={successMessage()}
          apiFailled={apiFailled}
        />
      )}
    </>
  );
};

export default SelectiveShowStockModalV2;
